import React, {useState} from 'react';
import {Form} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {MultiValue, SingleValue} from 'react-select/dist/declarations/src/types';
import i18n from 'i18n';

export interface SelectOption {
  value: any;
  label: string;
  isDisabled?: boolean;
}

export interface AsyncSelectFieldProps {
  id?: string;
  name: string;
  label?: string;
  value: SelectOption | SelectOption[] | null;
  onChange: (value: SelectOption | SelectOption[] | null) => void;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  isMulti?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  error?: string;
  touched?: boolean;
  debounceMs?: number;
  cacheOptions?: boolean;
  defaultOptions?: boolean | SelectOption[];
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  noOptionsMessage?: (obj: { inputValue: string }) => string;
  loadingMessage?: (obj: { inputValue: string }) => string;
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  selectAllValue?: any;
  currentUserId?: number | string;
  filterCurrentUser?: boolean;
  customStyles?: any;
  'data-cy'?: string;
}

const AsyncSelectField: React.FC<AsyncSelectFieldProps> = ({
    id,
    name,
    label,
    value,
    onChange,
    loadOptions,
    isMulti = false,
    placeholder,
    isDisabled = false,
    isRequired = false,
    error,
    touched,
    debounceMs = 280,
    cacheOptions = true,
    defaultOptions = true,
    hideSelectedOptions = false,
    closeMenuOnSelect,
    noOptionsMessage = () => i18n.t('jt_no_options_found.text'),
    loadingMessage = () => i18n.t('sp_loading.text'),
    enableSelectAll = false,
    selectAllLabel = i18n.t('jt_select_all.text'),
    selectAllValue = '*',
    currentUserId,
    filterCurrentUser = false,
    customStyles,
    'data-cy': dataCy,
}) => {
    const [allOptions, setAllOptions] = useState<SelectOption[]>([]);

    // Create debounced load function
    const debouncedLoadOptions = AwesomeDebouncePromise(loadOptions, debounceMs);

    const handleLoadOptions = async (inputValue: string): Promise<SelectOption[]> => {
        const results = await debouncedLoadOptions(inputValue);

        // Filter out current user if requested
        const filteredResults = filterCurrentUser && currentUserId
            ? results.filter(option => option.value !== currentUserId)
            : results;

        setAllOptions(filteredResults);

        // Add "Select All" option for multi-select if enabled
        if (isMulti && enableSelectAll && !inputValue && filteredResults.length > 0) {
            const selectAllOption: SelectOption = {
                label: selectAllLabel,
                value: selectAllValue,
            };
            return [selectAllOption, ...filteredResults];
        }

        return filteredResults;
    };

    const handleChange = (selectedValue: MultiValue<SelectOption> | SingleValue<SelectOption>) => {
        if (isMulti) {
            const multiValue = selectedValue as MultiValue<SelectOption>;

            // Handle "Select All" functionality
            if (enableSelectAll && multiValue && multiValue.length > 0) {
                const lastSelected = multiValue[multiValue.length - 1];
                if (lastSelected?.value === selectAllValue) {
                    onChange(allOptions);
                    return;
                }
            }

            onChange(multiValue ? [...multiValue] : []);
        } else {
            onChange(selectedValue as SingleValue<SelectOption>);
        }
    };

    const defaultCustomStyles = {
        valueContainer: (provided: any) => ({
            ...provided,
            maxHeight: '100px',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            overflowStyle: 'none',
            '&::-webkit-scrollbar': {
                width: 0,
                height: 0,
            },
        }),
    };

    const mergedStyles = customStyles
        ? {...defaultCustomStyles, ...customStyles}
        : defaultCustomStyles;

    return (
        <Form.Group className="mb-3" controlId={id || name}>
            {
                label && (
                    <Form.Label>
                        {label}
                        {isRequired && <span className="text-danger"> *</span>}
                    </Form.Label>
                )
            }

            <AsyncSelect
                id={id}
                name={name}
                value={value}
                onChange={handleChange}
                loadOptions={handleLoadOptions}
                isMulti={isMulti}
                placeholder={placeholder}
                isDisabled={isDisabled}
                cacheOptions={cacheOptions}
                defaultOptions={defaultOptions}
                hideSelectedOptions={hideSelectedOptions}
                closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
                noOptionsMessage={noOptionsMessage}
                loadingMessage={loadingMessage}
                styles={mergedStyles}
                data-cy={dataCy}
            />

            {
                touched && error && (
                    <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                        {error}
                    </Form.Control.Feedback>
                )
            }
        </Form.Group >
    );
};

export default AsyncSelectField;
